"use client";
import { Form } from "@/components/blocks";
import { Text } from "@/components/modules/FormFields/Text";
import { Textarea } from "@/components/modules/FormFields/Textarea";
import { useState, useEffect, useRef } from "react";

export const ContactForm = () => {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [formLoadTime, setFormLoadTime] = useState<number>(0);
  const honeypotRef = useRef<HTMLInputElement>(null);

  // Track when form loads for submission speed analysis
  useEffect(() => {
    setFormLoadTime(Date.now());
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    setLoading(true);
    e.preventDefault();

    const form = e.currentTarget;
    const submissionTime = Date.now();
    const timeTaken = submissionTime - formLoadTime;

    const formData = {
      name:
        (
          form.elements.namedItem(
            "first-last-name_firstLastName"
          ) as HTMLInputElement
        )?.value || "",
      email:
        (form.elements.namedItem("email-address_email") as HTMLInputElement)
          ?.value || "",
      phone:
        (form.elements.namedItem("phone-number_phone") as HTMLInputElement)
          ?.value || "",
      message:
        (form.elements.namedItem("message_message") as HTMLTextAreaElement)
          ?.value || "",
      honeypot: honeypotRef.current?.value || "", // Spam detection field
      timestamp: timeTaken, // Time taken to fill out form
    };

    try {
      const res = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (res.status === 429) {
        setStatus("Too many requests. Please try again later.");
      } else if (result.success) {
        setStatus("Thank you! Your message has been sent successfully.");
        form.reset();
        setFormLoadTime(Date.now()); // Reset timer for potential re-submission
      } else {
        setStatus(result.error || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Contact form error:", error);
      setStatus("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }
  return (
    <div className="px-4">
      <div className="text-center flex flex-col gap-8 mb-16">
        <div>
          <p className="text-[24px] text-primaryDark font-display">Phone</p>
          <a
            href="tel:8127775375"
            aria-label="Phone Number"
            rel="noopener noreferrer"
            target="_blank"
            className="text-[16px] text-primaryDark font-semibold"
          >
            (812) 777-5375
          </a>
        </div>
        <div>
          <p className="text-[24px] text-primaryDark font-display">
            Mailing Address
          </p>
          <a
            href="https://www.google.com/maps/place/5444+E+Indiana+St,+Evansville,+IN+47715/@37.990123,-87.475123,17z/data=!3m1!4b1!4m6!3m5!1s0x887f8c2d7a7e8b9b:0x2f5c5e5c5c5c5c5c!8m2!3d37.990123!4d-87.475123!16s%2Fg%2F11b6_7_7"
            aria-label="Mailing Address"
            rel="noopener noreferrer"
            target="_blank"
            className="text-[16px] text-primaryDark font-semibold"
          >
            5444 E Indiana Street, #353 Evansville, IN 47715
          </a>
        </div>
      </div>
      <div className="max-w-[747px] mx-auto rounded-[59px] py-14 md:py-[90px] px-8 md:px-4 bg-tertiary">
        <div className="max-w-[510px] mx-auto flex flex-col gap-8">
          <Form submitCopy="Send" onSubmit={handleSubmit} loading={loading}>
            {/* Honeypot field - hidden from users but visible to bots */}
            <input
              ref={honeypotRef}
              type="text"
              name="website"
              autoComplete="off"
              tabIndex={-1}
              style={{
                position: "absolute",
                left: "-9999px",
                top: "-9999px",
                visibility: "hidden",
                opacity: 0,
                height: 0,
                width: 0,
              }}
              aria-hidden="true"
            />

            <Text
              type="text"
              hideLabel
              disabled={loading}
              label="First & Last Name"
              id="firstLastName"
              placeholder="First & Last Name"
            />
            <Text
              type="email"
              hideLabel
              disabled={loading}
              required
              label="Email Address"
              id="email"
              placeholder="E-mail Address"
            />
            <Text
              type="tel"
              hideLabel
              disabled={loading}
              label="Phone Number"
              id="phone"
              placeholder="Phone Number"
            />
            <Textarea
              hideLabel
              disabled={loading}
              label="Message"
              id="message"
              placeholder="Message"
            />
            {status && (
              <div
                className={`text-center p-4 rounded-lg ${
                  status.includes("success") || status.includes("Thank you")
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                <p>{status}</p>
              </div>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};
